import type { WorldCity, WorldCountry } from "../api/types";
import { CityApi } from "../api/client";
import { cityPathForLanguage } from "../urls";
import { t } from "../i18n";

export async function chooseEntryCountry(loader: HTMLElement, api: CityApi): Promise<void> {
  if (!/\/city\/?$/.test(location.pathname)) return;
  const editor = document.body.dataset.cityEditor === "true";
  // A direct city link must enter that city, not get stuck at the world map.
  // sessionStorage can throw on restrictive mobile/private browsers.
  const requestedCity = new URL(location.href).searchParams.get("city")?.trim();
  let transfer: string | null = null;
  try {
    transfer = sessionStorage.getItem("city-entry-target");
    if (transfer) sessionStorage.removeItem("city-entry-target");
  } catch {
    // Storage is optional for navigation.
  }
  const navigation = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
  if (editor || requestedCity || navigation?.type === "reload" || /\/city\/(?:store|my-shop)\//.test(location.pathname)) return;
  loader.classList.add("city-world-entry");
  loader.replaceChildren();
  const panel = document.createElement("section"); panel.className = "city-world-entry__panel";
  const title = document.createElement("h1"); title.setAttribute("data-city-loading-title", ""); title.textContent = t("Website City World");
  const subtitle = document.createElement("p"); subtitle.textContent = t("Choose a country; switch cities later using Travel.");
  const status = document.createElement("p"); status.setAttribute("data-city-loading-message", ""); status.className = "city-world-entry__status"; status.textContent = t("Loading destinations…");
  panel.append(title, subtitle, status); loader.append(panel);
  const world = await api.world();
  const available = new Map(world.countries.map(country => [country.code.toUpperCase(), country]));
  if (!available.size) throw new Error("No accessible country is published.");
  // The detailed world SVG is only needed on the country picker. Keeping it
  // out of the city entry bundle saves parsing a large path catalog on phones.
  const { worldShapes } = await import("./worldShapes");
  const namespace = "http://www.w3.org/2000/svg";
  const map = document.createElementNS(namespace, "svg"); map.setAttribute("viewBox", "0 0 900 450"); map.setAttribute("aria-label", t("Available countries"));
  const buttons = document.createElement("div"); buttons.className = "city-world-entry__countries";
  const enter = (country: WorldCountry) => {
    const city: WorldCity = country.cities.find(c => c.capital) ?? country.cities[0];
    if (!city) return;
    try { sessionStorage.setItem("city-entry-target", city.slug); } catch { /* optional */ }
    status.textContent = t("Entering {country}…").replace("{country}", country.name);
    map.style.pointerEvents = "none"; buttons.querySelectorAll("button").forEach(b => b.disabled = true);
    location.assign(`${cityPathForLanguage(city.language.code)}?city=${encodeURIComponent(city.slug)}`);
  };
  for (const shape of worldShapes) {
    const path = document.createElementNS(namespace, "path"); path.setAttribute("d", shape.path); path.setAttribute("fill-rule", "evenodd");
    const country = available.get(shape.code);
    path.classList.add(country ? "is-available" : "is-unavailable");
    if (country) {
      path.setAttribute("tabindex", "0"); path.setAttribute("role", "button"); path.setAttribute("aria-label", country.name);
      const tip = document.createElementNS(namespace, "title"); tip.textContent = country.name; path.append(tip);
      path.addEventListener("click", () => enter(country));
      path.addEventListener("keydown", e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); enter(country); } });
    }
    map.append(path);
  }
  buttons.setAttribute("aria-label", t("Available countries"));
  for (const country of world.countries) {
    const button = document.createElement("button"); button.type = "button";
    button.textContent = country.code.toUpperCase() === "GB" ? "UK" : country.code.toUpperCase();
    button.title = country.name; button.setAttribute("aria-label", country.name);
    button.addEventListener("click", () => enter(country)); buttons.append(button);
  }
  status.textContent = t("Highlighted countries are open to visit.");
  panel.insertBefore(map, status); panel.insertBefore(buttons, status);
  return new Promise<void>(() => { /* Country choice navigates to the selected city. */ });
}
